import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { Dialog, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useUpdateCaregiverConsentsMutation } from "@api/graphql/types-and-hooks";
import { FronteraClient } from "@components/ClientTable/ClientTable";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";

const ManageVideoConsentFormDialog = ({ onClose, client }: { onClose: () => void; client: FronteraClient }) => {
  const queryClient = useQueryClient();
  const { isPending, mutateAsync } = useUpdateCaregiverConsentsMutation();

  const { onSubmit, formState, reset, RegisteredCheckboxButtonList } = useFormUtils<{
    consentingCaregiverIds: string[];
  }>({
    useNativeValidation: false,
    defaultValues: { consentingCaregiverIds: client.caregivers.filter((c) => c.consentGiven).map((c) => c.id) },
    mutationFn: async (params) => {
      // turn the array of selected caregiver IDs into an object of caregiver IDs to consent
      const newConsentByCaregiverId = params.consentingCaregiverIds.reduce<{ [caregiverId: string]: boolean }>(
        (acc, caregiverId) => {
          acc[caregiverId] = true;
          return acc;
        },
        {},
      );
      const caregiversToUpdate: { caregiverId: string; newConsent: boolean }[] = [];
      client.caregivers.forEach((caregiver) => {
        const newConsent = Boolean(newConsentByCaregiverId[caregiver.id]);
        if (caregiver.consentGiven !== newConsent) {
          // only update if consent has changed
          caregiversToUpdate.push({ caregiverId: caregiver.id, newConsent });
        }
      });
      if (caregiversToUpdate.length > 0) {
        await mutateAsync({
          consentUpdates: caregiversToUpdate.map((update) => ({
            learnerId: client.id,
            caregiverId: update.caregiverId,
            consentGiven: update.newConsent,
          })),
        });
        await queryClient.invalidateQueries({ queryKey: ["learners"] });
        await queryClient.invalidateQueries({ queryKey: ["learner-by-id", client.id] });
      }
      onClose();
    },
  });

  // why cant I just use defaultValues above? calling reset here seems to be necessary
  useEffect(() => {
    reset({ consentingCaregiverIds: client.caregivers.filter((c) => c.consentGiven).map((c) => c.id) });
  }, [client, reset]);

  return (
    <FormContainer onSubmit={onSubmit}>
      <Dialog
        title="Manage video consent"
        open={!!client}
        setOpen={onClose}
        primaryButton={{
          text: "Save",
          onClick: onSubmit,
          disabled: isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: onClose,
        }}
        size="lg"
      >
        <Paragraph>
          Select all caregivers who have signed the video recording consent. Only one signature is required to enable
          recording.
        </Paragraph>
        <RegisteredCheckboxButtonList
          formKey="consentingCaregiverIds"
          formState={formState}
          required={false}
          title="Select caregiver(s) who signed:"
          orientation="vertical-full-width"
          items={client.caregivers.map((caregiver) => ({
            id: caregiver.id,
            title: caregiver.name,
          }))}
        />
      </Dialog>
    </FormContainer>
  );
};

export default ManageVideoConsentFormDialog;
